import { Table2 } from "lucide-react";
import { useTranslate } from "ra-core";
import { useId, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export interface ChartLegendItem {
  label: string;
  color: string;
  /** Line series get a line swatch, fills a square */
  isLine?: boolean;
}

export interface ChartTableData {
  columns: string[];
  rows: { key: string; cells: string[] }[];
}

interface ChartCardProps {
  title: string;
  description?: string;
  legend?: ChartLegendItem[];
  /** Same numbers as the chart, readable without color */
  table: ChartTableData;
  /** Chart height in px */
  height?: number;
  isEmpty?: boolean;
  children: ReactNode;
}

const Legend = ({ items }: { items: ChartLegendItem[] }) => (
  <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
    {items.map((item) => (
      <li key={item.label} className="flex items-center gap-1.5">
        <span
          aria-hidden
          className={item.isLine ? "h-0.5 w-3 rounded" : "size-2.5 rounded-sm"}
          style={{ background: item.color }}
        />
        {item.label}
      </li>
    ))}
  </ul>
);

/** Chart frame: title, legend (2+ series) and a table view of the same data */
export const ChartCard = ({
  title,
  description,
  legend,
  table,
  height = 260,
  isEmpty,
  children,
}: ChartCardProps) => {
  const translate = useTranslate();
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="flex flex-row items-start justify-between gap-2 px-4">
        <div className="flex min-w-0 flex-col gap-1">
          <CardTitle className="text-sm font-semibold">{title}</CardTitle>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-pressed={showTable}
          aria-controls={tableId}
          onClick={() => setShowTable((value) => !value)}
          title={translate("crm.finance.overview.show_table")}
        >
          <Table2 />
          <span className="sr-only">
            {translate("crm.finance.overview.show_table")}
          </span>
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 px-4">
        {legend && legend.length > 1 && <Legend items={legend} />}
        {isEmpty ? (
          <p
            className="flex items-center justify-center text-sm text-muted-foreground"
            style={{ height }}
          >
            {translate("crm.finance.overview.no_data")}
          </p>
        ) : showTable ? (
          <div
            id={tableId}
            className="overflow-auto"
            style={{ maxHeight: height }}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  {table.columns.map((column) => (
                    <TableHead key={column}>{column}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {table.rows.map((row) => (
                  <TableRow key={row.key}>
                    {row.cells.map((cell, index) => (
                      <TableCell
                        key={index}
                        className={index > 0 ? "text-right" : undefined}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div style={{ height }} role="img" aria-label={title}>
            {children}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
